import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkle, ArrowRight, Check, Camera, FilmStrip,
  ArrowsClockwise, Trash, ListNumbers, Lightning, MapPin,
  UploadSimple, BookmarkSimple, Lock, FolderOpen,
  CaretUp, CaretDown, Shuffle, Stack,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import CostChip from "./CostChip";
import { assetUrl } from "../../lib/assetUrl";
import { useEnvDegraded } from "../../context/SystemHealthContext";

// Tiny inline pill explaining why a Generate button is disabled.
function EnvDegradedNote({ reason, testid }) {
  return (
    <span data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border hairline text-[11px] bg-destructive/10 text-destructive">
      <span aria-hidden>⚠</span>
      <span>Disabled — env drift: <b>{reason}</b></span>
    </span>
  );
}

/* Step-by-step Workflow Composer (Iter-38, extended in Iter-39 round 3)
   Generates the output of each phase inline so the admin can review + edit
   before proceeding. Lives alongside the legacy one-shot composer; activated
   via the mode toggle at the top of WorkflowComposer.jsx.

   Iter-39 round 3 additions:
   - ModelInUsePill at the top of every stage (which model is being used)
   - Save-to-library button on scripts/images/videos
   - Upload-from-disk buttons (script JSON paste, image PNG, video MP4)
   - Library picker modal to re-apply saved assets
   - Per-stage model banner at the top of the wizard */

const STAGE_LABELS = {
  0: { id: "script",   label: "Script",        Icon: Sparkle,   color: "#C85A32" },
  1: { id: "image",    label: "Image + Cast",  Icon: Camera,    color: "#5B7CFA" },
  2: { id: "video",    label: "Video",         Icon: FilmStrip, color: "#B89B72" },
  3: { id: "timeline", label: "Timeline",      Icon: ListNumbers, color: "#5FB37C" },
};

// Top-of-stage pill: "Generating with Claude Sonnet 4.5 · ₹12/storyboard"
function ModelInUsePill({ stage, model, testid }) {
  if (!model) {
    return (
      <div className="text-[11px] text-muted-foreground card-flat p-2 border-l-2 border-muted"
           data-testid={testid}>
        No {stage.toLowerCase()} model selected. Pick one below.
      </div>
    );
  }
  return (
    <div className="card-flat p-2 border-l-2 border-[hsl(var(--primary))] inline-flex items-center gap-2 text-[11px]"
         data-testid={testid}>
      <span className="text-muted-foreground">{stage} model in use:</span>
      <span className="font-medium">{model.label}</span>
      <span className="text-[hsl(var(--primary))]">· ₹{model.cost_per_unit_inr}/{model.unit}</span>
      <span className="text-[9px] text-muted-foreground font-mono opacity-60">{model.model_id}</span>
    </div>
  );
}

export default function StepByStepComposer({ onJumpToDraft }) {
  const [stage, setStage] = useState(0);
  const [registry, setRegistry] = useState(null);
  const [castList, setCastList] = useState([]);

  // Step 1 inputs
  const [prompt, setPrompt] = useState("");
  const [sceneCount, setSceneCount] = useState(5);
  const [scriptModelId, setScriptModelId] = useState("");
  const [imageModelId, setImageModelId] = useState("");
  const [videoModelId, setVideoModelId] = useState("");
  const [castIds, setCastIds] = useState([]);

  // Live workspace
  const [draftId, setDraftId] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/social/models-registry").then((r) => r.data).catch(() => null),
      api.get("/admin/social/cast").then((r) => r.data).catch(() => []),
    ]).then(([reg, cast]) => {
      setRegistry(reg);
      setCastList(cast || []);
      if (reg) {
        const pick = (s) => {
          const items = reg[s] || [];
          const def = items.find((m) => m.default && m.enabled);
          return def?.model_id || items.find((m) => m.enabled)?.model_id || "";
        };
        setScriptModelId((v) => v || pick("script"));
        setImageModelId((v) => v || pick("image"));
        setVideoModelId((v) => v || pick("video"));
      }
    });
  }, []);

  const refreshScenes = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/social/drafts/${id}/scenes`);
      setScenes(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load scenes");
    }
  }, []);

  // -------------------- Step 1 — Generate scripts --------------------
  const generateScripts = async () => {
    if (prompt.trim().length < 4) {
      toast.error("Enter at least a 4-character topic.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/social/workflow-run", {
        prompt, scene_count: sceneCount,
        script_model_id: scriptModelId,
        image_model_id: imageModelId,
        video_model_id: videoModelId,
        cast_member_ids: castIds,
        auto_generate_assets: false,
      });
      const id = data.draft_id;
      setDraftId(id);
      toast.success(`Drafting ${sceneCount} scenes…`);
      // Poll for up to 60s
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: s } = await api.get(`/admin/social/drafts/${id}/scenes`);
        if (s && s.length > 0) {
          setScenes(s);
          toast.success(`Generated ${s.length} scenes — review and edit.`);
          return;
        }
        const { data: d } = await api.get(`/admin/social/drafts/${id}`);
        if (d?.status === "error") {
          toast.error(d.error || "Script generation failed — see Error log.");
          return;
        }
      }
      toast.error("Timed out waiting for scenes. Open the Drafts tab to track progress.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // -------------------- Common — save scene edits --------------------
  const saveScene = async (sceneId, patch) => {
    try {
      await api.put(`/admin/social/scenes/${sceneId}`, patch);
      setScenes((arr) => arr.map((s) => s.id === sceneId ? { ...s, ...patch } : s));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // -------------------- Step 2 — Generate images --------------------
  const generateImages = async () => {
    if (!draftId) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/social/drafts/${draftId}/generate-images?include_location=true`
      );
      toast.success(data.message || `Queued ${data.queued} scenes for image gen.`);
      // Poll for image completion (up to ~5 min). Each image takes 10-30s.
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 10000));
        const { data: s } = await api.get(`/admin/social/drafts/${draftId}/scenes`);
        setScenes(s || []);
        const stillRunning = (s || []).some((x) =>
          x.image_status === "rendering" || x.image_status === "queued"
        );
        if (!stillRunning) {
          toast.success("All images ready.");
          break;
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const regenImage = async (sceneId, kind) => {
    setBusy(true);
    try {
      await api.post(`/admin/social/scenes/${sceneId}/regenerate-image?kind=${kind}`);
      toast.success(`${kind === "character" ? "Character" : "Location"} image regenerated.`);
      await refreshScenes(draftId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // -------------------- Step 3 — Generate videos --------------------
  const generateVideos = async () => {
    if (!draftId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/social/drafts/${draftId}/generate-videos`);
      toast.success(`Queued ${data.queued} scene videos. They will appear as they finish.`);
      // Poll for completion (up to 5 min)
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 10000));
        const { data: s } = await api.get(`/admin/social/drafts/${draftId}/scenes`);
        setScenes(s || []);
        const stillRunning = (s || []).some((x) => x.status === "regenerating" || x.status === "queued");
        if (!stillRunning) {
          toast.success("All video renders complete.");
          break;
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const regenVideo = async (sceneId, patch) => {
    setBusy(true);
    try {
      await api.post(`/admin/social/scenes/${sceneId}/regenerate-video`, patch);
      toast.success("Video regeneration started.");
      await refreshScenes(draftId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const deleteScene = async (sceneId) => {
    if (!window.confirm("Delete this scene? Image + video assets are also removed.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/social/scenes/${sceneId}`);
      setScenes((arr) => arr.filter((s) => s.id !== sceneId));
      toast.success("Scene deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // -------------------- Iter-39 r3 — Upload scripts JSON --------------------
  const [showUpload, setShowUpload] = useState(false);
  const uploadScripts = async (payload) => {
    try {
      // If no draft yet, create one first via workflow-run with empty topic
      // and immediately replace scenes via upload-scripts.
      let id = draftId;
      if (!id) {
        const { data } = await api.post("/admin/social/workflow-run", {
          prompt: payload.topic || "(uploaded scripts)",
          scene_count: payload.scenes.length,
          auto_generate_assets: false,
        });
        id = data.draft_id;
        setDraftId(id);
        // Wait briefly so the draft row is present.
        await new Promise((r) => setTimeout(r, 1500));
      }
      const { data: up } = await api.post(`/admin/social/drafts/${id}/upload-scripts`, {
        scenes: payload.scenes, replace_existing: true,
      });
      toast.success(`Uploaded ${up.count} scenes.`);
      await refreshScenes(id);
      setShowUpload(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Iter-40 round 3 — Library picker for any kind.
  const [libraryPick, setLibraryPick] = useState(null); // {kind, sceneId, imageKind?}
  const applyFromLibrary = async (libId) => {
    if (!libraryPick) return;
    const { kind, sceneId, imageKind } = libraryPick;
    try {
      if (kind === "script") {
        await api.post(`/admin/social/scenes/${sceneId}/apply-library-script`, { library_id: libId });
      } else if (kind === "image") {
        await api.post(`/admin/social/scenes/${sceneId}/apply-library-image`,
          { library_id: libId, kind: imageKind || "character" });
      } else if (kind === "video") {
        await api.post(`/admin/social/scenes/${sceneId}/apply-library-video`, { library_id: libId });
      }
      toast.success("Applied from library — refreshing scenes.");
      setLibraryPick(null);
      if (draftId) await refreshScenes(draftId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // -------------------- Iter-41 r2 — bulk library + bundles + reorder -----
  const [showBulkLibrary, setShowBulkLibrary] = useState(false);

  // Ensure a draft exists before applying anything — creates an empty draft
  // if the admin opens the library picker before generating scripts.
  const ensureDraft = async () => {
    if (draftId) return draftId;
    const { data } = await api.post("/admin/social/workflow-run", {
      prompt: "(library bulk apply)",
      scene_count: 1,
      auto_generate_assets: false,
    });
    setDraftId(data.draft_id);
    await new Promise((r) => setTimeout(r, 1200));
    return data.draft_id;
  };

  const applyBulkLibrary = async ({ libraryIds, bundleIds, mode }) => {
    if (!libraryIds.length && !bundleIds.length) {
      toast.error("Pick at least one entry from the library.");
      return;
    }
    try {
      const id = await ensureDraft();
      // Bulk-apply non-bundle items first, then iterate bundles.
      let totalInserted = 0;
      if (libraryIds.length) {
        const { data } = await api.post(
          `/admin/social/drafts/${id}/apply-library-bulk`,
          { library_ids: libraryIds, mode }
        );
        totalInserted += data.count || 0;
        if (data.skipped?.length) {
          toast.warning(`Skipped ${data.skipped.length} entries — see console.`);
          console.warn("Library bulk apply skipped", data.skipped);
        }
      }
      // Bundles always APPEND (even if mode=replace was used, replace ran once).
      const bundleMode = (mode === "replace" && libraryIds.length === 0) ? "replace" : "append";
      for (const bid of bundleIds) {
        const { data } = await api.post(
          `/admin/social/drafts/${id}/apply-bundle`,
          { bundle_id: bid, mode: bundleMode }
        );
        totalInserted += data.count || 0;
      }
      toast.success(`Added ${totalInserted} scene${totalInserted === 1 ? "" : "s"} from library.`);
      setShowBulkLibrary(false);
      await refreshScenes(id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveAsBundle = async () => {
    if (!draftId || scenes.length === 0) {
      toast.error("Generate or upload some scenes first.");
      return;
    }
    const name = window.prompt("Name this bundle (optional):", "");
    if (name === null) return; // cancelled
    try {
      const { data } = await api.post("/admin/social/library/bundles", {
        draft_id: draftId, name: name || "",
      });
      toast.success(`Saved bundle (${data.scene_count} scenes).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reorderScene = async (sceneId, direction) => {
    const idx = scenes.findIndex((s) => s.id === sceneId);
    if (idx < 0) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= scenes.length) return;
    const next = scenes.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setScenes(next.map((s, i) => ({ ...s, index: i + 1 })));
    try {
      await api.post(`/admin/social/drafts/${draftId}/scenes/reorder`, {
        scene_ids: next.map((s) => s.id),
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      await refreshScenes(draftId);
    }
  };

  const shuffleScenes = async () => {
    if (!draftId || scenes.length < 2) return;
    if (!window.confirm("Shuffle scene order? You can undo by reordering manually.")) return;
    const arr = scenes.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setScenes(arr.map((s, i) => ({ ...s, index: i + 1 })));
    try {
      await api.post(`/admin/social/drafts/${draftId}/scenes/reorder`, {
        scene_ids: arr.map((s) => s.id),
      });
      toast.success("Scenes shuffled.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      await refreshScenes(draftId);
    }
  };

  const deleteAllScenes = async () => {
    if (!draftId || scenes.length === 0) return;
    if (!window.confirm(`Delete all ${scenes.length} scenes? Image + video assets are removed too.`)) return;
    setBusy(true);
    try {
      for (const s of scenes) {
        await api.delete(`/admin/social/scenes/${s.id}`);
      }
      setScenes([]);
      toast.success("All scenes deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      await refreshScenes(draftId);
    } finally { setBusy(false); }
  };

  // -------------------- Render --------------------
  return (
    <div className="space-y-5" data-testid="step-by-step-composer">
      {/* Iter-39 r3 — Workflow-tab top banner: all three selected models at a glance */}
      <div className="card-flat p-3 grid sm:grid-cols-3 gap-2" data-testid="sbs-models-banner">
        {[
          ["Script", scriptModelId, registry?.script || []],
          ["Image",  imageModelId,  registry?.image  || []],
          ["Video",  videoModelId,  registry?.video  || []],
        ].map(([stageLbl, id, models]) => {
          const m = models.find((x) => x.model_id === id);
          return (
            <div key={stageLbl} className="text-[11px] leading-snug"
                 data-testid={`sbs-banner-${stageLbl.toLowerCase()}`}>
              <div className="text-muted-foreground">{stageLbl} model</div>
              <div className="font-medium text-foreground truncate">
                {m?.label || <span className="text-muted-foreground">(none)</span>}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono truncate">
                {m?.model_id || "—"} {m && <span className="text-[hsl(var(--primary))]">· ₹{m.cost_per_unit_inr}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Stage rail */}
      <div className="card-flat p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {[0, 1, 2, 3].map((i) => {
              const s = STAGE_LABELS[i];
              const active = i === stage;
              const done = i < stage;
              const reachable = i === 0 || draftId;
              return (
                <button
                  key={s.id}
                  disabled={!reachable}
                  onClick={() => reachable && setStage(i)}
                  data-testid={`sbs-stage-${s.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded transition-colors disabled:opacity-30
                    ${active ? "bg-[hsl(var(--primary))] text-white" : done ? "bg-[hsl(var(--muted))] text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {done ? <Check size={12} weight="bold" /> : <s.Icon size={12} weight="duotone" style={{ color: active ? "white" : s.color }} />}
                  {i + 1}. {s.label}
                  {i < 3 && <ArrowRight size={11} className="opacity-50 ml-1" />}
                </button>
              );
            })}
          </div>
          <button onClick={() => setShowUpload(true)}
            className="btn-ghost px-3 py-1.5 text-[11px] inline-flex items-center gap-1"
            data-testid="sbs-upload-scripts-open">
            <UploadSimple size={11} weight="bold" /> Upload scripts (JSON)
          </button>
          <button onClick={() => setShowBulkLibrary(true)}
            className="btn-ghost px-3 py-1.5 text-[11px] inline-flex items-center gap-1"
            title="Pick saved scripts, images, videos, or whole bundles — appends as new scenes (zero LLM cost)."
            data-testid="sbs-bulk-library-open">
            <FolderOpen size={11} weight="duotone" /> Use from library
          </button>
          {draftId && scenes.length > 0 && (
            <button onClick={saveAsBundle}
              className="btn-ghost px-3 py-1.5 text-[11px] inline-flex items-center gap-1"
              title="Save the entire current draft (all scenes + images + videos) as a reusable bundle."
              data-testid="sbs-save-bundle">
              <Stack size={11} weight="duotone" /> Save as bundle
            </button>
          )}
          {draftId && scenes.length >= 2 && (
            <button onClick={shuffleScenes}
              className="btn-ghost px-3 py-1.5 text-[11px] inline-flex items-center gap-1"
              title="Randomly reorder all scenes."
              data-testid="sbs-shuffle-scenes">
              <Shuffle size={11} weight="duotone" /> Shuffle
            </button>
          )}
          {draftId && scenes.length > 0 && (
            <button onClick={deleteAllScenes}
              className="btn-ghost px-3 py-1.5 text-[11px] inline-flex items-center gap-1 text-destructive"
              title="Hard-delete every scene on this draft."
              data-testid="sbs-delete-all-scenes">
              <Trash size={11} weight="bold" /> Delete all
            </button>
          )}
        </div>
        {draftId && (
          <div className="text-[10px] text-muted-foreground mt-2">
            Draft <span className="font-mono">{draftId.slice(-8)}</span> · {scenes.length} scene{scenes.length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* Stage bodies */}
      {stage === 0 && (
        <StageScript
          registry={registry} prompt={prompt} setPrompt={setPrompt}
          sceneCount={sceneCount} setSceneCount={setSceneCount}
          scriptModelId={scriptModelId} setScriptModelId={setScriptModelId}
          scenes={scenes} saveScene={saveScene}
          onGenerate={generateScripts} onContinue={() => setStage(1)}
          busy={busy}
          openLibraryPick={(sceneId) => setLibraryPick({ kind: "script", sceneId })}
          reorderScene={reorderScene}
          deleteScene={deleteScene}
        />
      )}
      {stage === 1 && (
        <StageImage
          registry={registry} imageModelId={imageModelId} setImageModelId={setImageModelId}
          castList={castList} setCastList={setCastList}
          castIds={castIds} setCastIds={setCastIds}
          scenes={scenes} saveScene={saveScene}
          onGenerate={generateImages} regenImage={regenImage}
          onContinue={() => setStage(2)} busy={busy}
          openLibraryPick={(sceneId, imageKind) => setLibraryPick({ kind: "image", sceneId, imageKind })}
        />
      )}
      {stage === 2 && (
        <StageVideo
          registry={registry} videoModelId={videoModelId} setVideoModelId={setVideoModelId}
          scenes={scenes} saveScene={saveScene}
          onGenerate={generateVideos} regenVideo={regenVideo}
          deleteScene={deleteScene}
          onContinue={() => { setStage(3); if (draftId && onJumpToDraft) onJumpToDraft(draftId); }}
          busy={busy}
          openLibraryPick={(sceneId) => setLibraryPick({ kind: "video", sceneId })}
        />
      )}
      {stage === 3 && (
        <StageTimelineHandoff draftId={draftId} onJumpToDraft={onJumpToDraft} />
      )}

      {showUpload && (
        <UploadScriptsModal onClose={() => setShowUpload(false)} onSubmit={uploadScripts} />
      )}

      {libraryPick && (
        <LibraryPickerModal
          kind={libraryPick.kind}
          onClose={() => setLibraryPick(null)}
          onPick={applyFromLibrary}
        />
      )}

      {showBulkLibrary && (
        <BulkLibraryPickerModal
          onClose={() => setShowBulkLibrary(false)}
          onApply={applyBulkLibrary}
          hasScenes={scenes.length > 0}
        />
      )}
    </div>
  );
}

// Iter-40 round 3 — Library picker modal. Loads /library?kind=... and lets
// the admin click one to apply to the current scene.
function LibraryPickerModal({ kind, onClose, onPick }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/admin/social/library?kind=${kind}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [kind]);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-4xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="sbs-library-picker-modal">
        <div className="eyebrow">Pick a saved {kind} from your library</div>
        {items === null ? (
          <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground py-6 text-center">
            No saved {kind}s yet. Click the bookmark icon on a scene to save it.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => onPick(it.id)}
                data-testid={`sbs-library-pick-${it.id}`}
                className="text-left card-flat p-2 hover:border-[hsl(var(--primary))] border-2 border-transparent"
              >
                {kind === "image" && it.asset_url && (
                  <img src={assetUrl(it.asset_url)} alt={it.name} className="w-full aspect-video object-cover border hairline mb-2" />
                )}
                {kind === "video" && it.asset_url && (
                  <video src={assetUrl(it.asset_url)} className="w-full aspect-video bg-black border hairline mb-2"
                    aria-label={`Saved video ${it.name}`}>
                    <track kind="captions" />
                  </video>
                )}
                {kind === "script" && (
                  <div className="text-[10px] italic text-muted-foreground line-clamp-3 mb-2">
                    “{it.preview || "(no preview)"}”
                  </div>
                )}
                <div className="text-sm font-medium truncate">{it.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {it.image_kind || ""} {it.size_bytes ? `· ${(it.size_bytes / 1_048_576).toFixed(2)} MB` : ""}
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end pt-2">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Iter-41 r2 — Bulk library picker modal. Four tabs (Bundles · Scripts ·
// Images · Videos), multi-select via checkboxes, append-or-replace mode,
// one Apply button at the bottom that calls applyBulkLibrary().
function BulkLibraryPickerModal({ onClose, onApply, hasScenes }) {
  const TABS = [
    { id: "bundle", label: "Bundles", hint: "Whole-draft snapshots — applies all scenes in order." },
    { id: "script", label: "Scripts", hint: "Text-only — zero LLM cost on apply." },
    { id: "image",  label: "Images",  hint: "Saved character / location images become new scenes." },
    { id: "video",  label: "Videos",  hint: "Saved video clips become new scenes." },
  ];
  const [tab, setTab] = useState("bundle");
  const [byKind, setByKind] = useState({ bundle: null, script: null, image: null, video: null });
  const [selected, setSelected] = useState({}); // { libId: true }
  const [mode, setMode] = useState(hasScenes ? "append" : "replace");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (k) => {
    setByKind((m) => ({ ...m, [k]: m[k] }));
    try {
      const { data } = await api.get(`/admin/social/library?kind=${k}`);
      setByKind((m) => ({ ...m, [k]: data || [] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Library load failed");
      setByKind((m) => ({ ...m, [k]: [] }));
    }
  }, []);

  useEffect(() => {
    if (byKind[tab] === null) load(tab);
  }, [tab, byKind, load]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  };

  const items = byKind[tab];
  const selectedCount = Object.keys(selected).length;

  const submit = async () => {
    const ids = Object.keys(selected);
    if (!ids.length) {
      toast.error("Pick at least one entry.");
      return;
    }
    // Separate bundle ids from regular library ids — the backend uses two endpoints.
    const allItems = Object.values(byKind).flat().filter(Boolean);
    const bundleIds = [];
    const libraryIds = [];
    for (const id of ids) {
      const item = allItems.find((it) => it && it.id === id);
      if (item && item.kind === "bundle") bundleIds.push(id);
      else libraryIds.push(id);
    }
    setBusy(true);
    try {
      await onApply({ libraryIds, bundleIds, mode });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-5xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto"
        data-testid="sbs-bulk-library-modal">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="eyebrow">Use from library</div>
            <p className="text-[11px] text-muted-foreground mt-1 max-w-2xl">
              Multi-select saved entries — each becomes a new scene on the current draft.
              Bundles apply their full scene list in order. Skips all LLM cost on apply.
            </p>
          </div>
          <div className="text-[11px] text-muted-foreground">
            {selectedCount} selected
          </div>
        </div>

        {/* Tabs */}
        <div className="inline-flex rounded border hairline overflow-hidden text-[11px]" data-testid="sbs-bulk-tabs">
          {TABS.map((t) => (
            <button key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`sbs-bulk-tab-${t.id}`}
              className={`px-3 py-1.5 ${tab === t.id ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-muted/40"}`}>
              {t.label} {byKind[t.id]?.length ? `(${byKind[t.id].length})` : ""}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground italic">
          {TABS.find((t) => t.id === tab)?.hint}
        </p>

        {/* Grid */}
        {items === null ? (
          <div className="text-xs text-muted-foreground py-12 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground py-12 text-center" data-testid="sbs-bulk-empty">
            No saved {tab}s yet. Save assets from scene cards (bookmark icon) to populate this list.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {items.map((it) => {
              const isSel = !!selected[it.id];
              return (
                <button key={it.id}
                  onClick={() => toggle(it.id)}
                  data-testid={`sbs-bulk-item-${it.id}`}
                  className={`text-left card-flat p-2 border-2 transition-colors
                    ${isSel ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-transparent hover:border-muted-foreground/30"}`}>
                  <div className="flex items-start gap-2">
                    <span aria-hidden className={`h-4 w-4 mt-0.5 inline-flex items-center justify-center border hairline shrink-0
                      ${isSel ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : ""}`}>
                      {isSel && <Check size={10} weight="bold" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      {tab === "image" && it.asset_url && (
                        <img src={assetUrl(it.asset_url)} alt={it.name} className="w-full aspect-video object-cover border hairline mb-2" />
                      )}
                      {tab === "video" && it.asset_url && (
                        <video src={assetUrl(it.asset_url)} className="w-full aspect-video bg-black border hairline mb-2"
                          aria-label={`Saved video ${it.name}`}>
                          <track kind="captions" />
                        </video>
                      )}
                      {tab === "script" && (
                        <div className="text-[10px] italic text-muted-foreground line-clamp-3 mb-2">
                          “{it.preview || "(no preview)"}”
                        </div>
                      )}
                      {tab === "bundle" && (
                        <div className="text-[11px] text-muted-foreground mb-2 inline-flex items-center gap-1">
                          <Stack size={11} weight="duotone" /> {it.scene_count || 0} scenes
                          {it.size_bytes ? ` · ${(it.size_bytes / 1_048_576).toFixed(2)} MB` : ""}
                        </div>
                      )}
                      <div className="text-sm font-medium truncate">{it.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {it.image_kind || ""}
                        {tab !== "bundle" && it.size_bytes ? ` · ${(it.size_bytes / 1_048_576).toFixed(2)} MB` : ""}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Mode toggle + Apply */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t hairline">
          <div className="inline-flex items-center gap-2 text-[11px]">
            <span className="text-muted-foreground">When applying:</span>
            <div className="inline-flex border hairline rounded overflow-hidden" data-testid="sbs-bulk-mode">
              <button onClick={() => setMode("append")}
                className={`px-3 py-1 ${mode === "append" ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-muted/40"}`}
                data-testid="sbs-bulk-mode-append">Append</button>
              <button onClick={() => setMode("replace")}
                className={`px-3 py-1 ${mode === "replace" ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-muted/40"}`}
                data-testid="sbs-bulk-mode-replace">Replace</button>
            </div>
            <span className="text-muted-foreground italic">
              {mode === "append" ? "Picked items become new scenes after existing ones." : "All existing scenes are deleted first."}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs"
              data-testid="sbs-bulk-cancel">Cancel</button>
            <button onClick={submit} disabled={busy || !selectedCount}
              className="btn-primary px-4 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="sbs-bulk-apply">
              <Check size={12} weight="bold" />
              {busy ? "Applying…" : `Apply (${selectedCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Iter-39 r3 — paste a JSON array of scene objects to skip the LLM script step.
function UploadScriptsModal({ onClose, onSubmit }) {
  const [topic, setTopic] = useState("");
  const [raw, setRaw] = useState(JSON.stringify([
    { title: "Scene 1", script: "Voice-over text…", image_prompt: "…", video_prompt: "…", duration: 6 },
    { title: "Scene 2", script: "Voice-over text…", image_prompt: "…", video_prompt: "…", duration: 6 },
  ], null, 2));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    let scenes;
    try {
      scenes = JSON.parse(raw);
      if (!Array.isArray(scenes) || !scenes.length) throw new Error("must be a non-empty array");
    } catch (e) {
      toast.error("Invalid JSON: " + e.message);
      return;
    }
    setBusy(true);
    try { await onSubmit({ topic, scenes }); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-3xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="sbs-upload-modal">
        <div className="eyebrow">Upload scripts (JSON)</div>
        <p className="text-xs text-muted-foreground">
          Paste an array of scene objects. Each scene supports: title, script, image_prompt,
          video_prompt, caption, location_image_prompt, duration (sec). Replaces ALL existing
          scenes in this draft.
        </p>
        <label className="text-xs block">
          <span className="text-muted-foreground block mb-1">Topic (optional — used for the new draft's name)</span>
          <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs block">
          <span className="text-muted-foreground block mb-1">JSON</span>
          <textarea rows={14} value={raw} onChange={(e) => setRaw(e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs font-mono"
            data-testid="sbs-upload-json" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs">Cancel</button>
          <button onClick={submit} disabled={busy}
            className="btn-primary px-4 py-1.5 text-xs disabled:opacity-50"
            data-testid="sbs-upload-submit">
            {busy ? "Uploading…" : "Upload scripts"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Stage 1 — Script -------------------- */
function StageScript({ registry, prompt, setPrompt, sceneCount, setSceneCount,
                       scriptModelId, setScriptModelId, scenes, saveScene,
                       onGenerate, onContinue, busy, openLibraryPick,
                       reorderScene, deleteScene }) {
  const models = (registry?.script || []).filter((m) => m.enabled);
  const hasScenes = scenes.length > 0;
  const selectedModel = models.find((m) => m.model_id === scriptModelId);
  const { degraded, reason } = useEnvDegraded();
  const blockTitle = degraded ? `Disabled — fix environment first. ${reason}` : "";
  return (
    <div className="space-y-4" data-testid="sbs-stage-1">
      <ModelInUsePill stage="Script" model={selectedModel} testid="sbs-script-model-pill" />
      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Stage 1 · Generate scripts</div>
        <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
          Pick a script model, type a hook, then click <b>Generate scripts</b>. Each scene becomes
          editable below — tweak voiceover, image prompt, video prompt, on-screen caption, or
          duration before moving to the next stage.
        </p>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="sbs-script-models">
          {models.map((m) => (
            <button key={m.id}
              onClick={() => setScriptModelId(m.model_id)}
              data-testid={`sbs-script-model-${m.model_id}`}
              className={`text-left card-flat p-3 border-2 transition-colors
                ${m.model_id === scriptModelId ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-transparent hover:border-muted-foreground/30"}`}>
              <div className="text-sm font-medium">{m.label}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{m.provider} · {m.model_id}</div>
              <div className="text-[11px] text-[hsl(var(--primary))] font-medium mt-2">
                ₹{m.cost_per_unit_inr}/{m.unit}
              </div>
            </button>
          ))}
        </div>
        <textarea rows={3}
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. How a 30-something Bengaluru engineer decides between renting in Whitefield vs buying a Rs 1.2 Cr flat in Yelahanka."
          className="w-full bg-background border hairline px-3 py-2 text-sm mt-4"
          data-testid="sbs-script-prompt"
        />
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <label className="text-xs">
            Scenes: <input type="number" min={2} max={10}
              value={sceneCount} onChange={(e) => setSceneCount(parseInt(e.target.value, 10) || 5)}
              className="bg-background border hairline px-2 py-1 text-xs w-16"
              data-testid="sbs-script-scenes" />
          </label>
          <button onClick={onGenerate} disabled={busy || degraded}
            title={blockTitle || undefined}
            className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="sbs-script-generate">
            <Sparkle size={12} weight="duotone" />
            {busy ? "Generating…" : (hasScenes ? "Regenerate scripts" : "Generate scripts")}
          </button>
          <CostChip stage="script" sceneCount={sceneCount} model={selectedModel}
            label="Est. for this run" testid="sbs-script-cost-chip" />
          {degraded && <EnvDegradedNote reason={reason} testid="sbs-script-env-note" />}
        </div>
      </div>

      {hasScenes && (
        <div className="space-y-3" data-testid="sbs-script-scene-list">
          {scenes.map((s, i) => (
            <SceneScriptCard key={s.id} scene={s} saveScene={saveScene}
              openLibraryPick={() => openLibraryPick && openLibraryPick(s.id)}
              onMoveUp={reorderScene && i > 0 ? () => reorderScene(s.id, "up") : null}
              onMoveDown={reorderScene && i < scenes.length - 1 ? () => reorderScene(s.id, "down") : null}
              onDelete={deleteScene ? () => deleteScene(s.id) : null} />
          ))}
          <div className="flex justify-end">
            <button onClick={onContinue}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
              data-testid="sbs-script-continue">
              Continue to Image + Cast <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SceneScriptCard({ scene, saveScene, openLibraryPick, onMoveUp, onMoveDown, onDelete }) {
  const [local, setLocal] = useState({
    script: scene.script || "",
    image_prompt: scene.image_prompt || "",
    video_prompt: scene.video_prompt || "",
    caption: scene.caption || "",
    duration: scene.duration || 6,
  });
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setLocal((l) => ({ ...l, [k]: v })); setDirty(true); };
  const persist = async () => {
    await saveScene(scene.id, local);
    setDirty(false);
    toast.success(`Scene ${scene.index} saved.`);
  };
  const saveToLibrary = async () => {
    try {
      await api.post("/admin/social/library/scripts", {
        scene_id: scene.id, name: scene.title || `Scene ${scene.index}`,
      });
      toast.success(`Saved scene ${scene.index} script to library.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="card-flat p-4" data-testid={`sbs-script-card-${scene.id}`}>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          {onMoveUp ? (
            <button onClick={onMoveUp} className="btn-ghost px-1.5 py-0.5 text-[10px]"
              title="Move up" data-testid={`sbs-script-up-${scene.id}`}>
              <CaretUp size={11} weight="bold" />
            </button>
          ) : <span className="w-6" aria-hidden />}
          {onMoveDown ? (
            <button onClick={onMoveDown} className="btn-ghost px-1.5 py-0.5 text-[10px]"
              title="Move down" data-testid={`sbs-script-down-${scene.id}`}>
              <CaretDown size={11} weight="bold" />
            </button>
          ) : <span className="w-6" aria-hidden />}
          <div className="eyebrow">Scene {scene.index} · {scene.title || "Untitled"}</div>
        </div>
        <div className="inline-flex items-center gap-2">
          {openLibraryPick && (
            <button onClick={openLibraryPick} className="btn-ghost px-2 py-1 text-[10px] inline-flex items-center gap-1"
              data-testid={`sbs-script-pick-library-${scene.id}`}
              title="Replace this scene with a saved script from the library.">
              <FolderOpen size={10} weight="duotone" /> Use from library
            </button>
          )}
          <button onClick={saveToLibrary} className="btn-ghost px-2 py-1 text-[10px] inline-flex items-center gap-1"
            data-testid={`sbs-script-save-library-${scene.id}`}
            title="Save this script (text + prompts) to the library for future reuse.">
            <BookmarkSimple size={10} weight="duotone" /> Save to library
          </button>
          {onDelete && (
            <button onClick={onDelete} className="btn-ghost px-2 py-1 text-[10px] text-destructive inline-flex items-center gap-1"
              data-testid={`sbs-script-delete-${scene.id}`}
              title="Delete this scene (image + video assets are removed too).">
              <Trash size={10} weight="bold" />
            </button>
          )}
          {dirty && (
            <button onClick={persist} className="btn-primary px-3 py-1 text-[11px]"
              data-testid={`sbs-script-save-${scene.id}`}>Save edits</button>
          )}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <label className="text-xs block">
          <span className="text-muted-foreground block mb-1">Voiceover</span>
          <textarea rows={3} value={local.script} onChange={(e) => set("script", e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs block">
          <span className="text-muted-foreground block mb-1">Image prompt</span>
          <textarea rows={3} value={local.image_prompt} onChange={(e) => set("image_prompt", e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs block">
          <span className="text-muted-foreground block mb-1">Video prompt</span>
          <textarea rows={2} value={local.video_prompt} onChange={(e) => set("video_prompt", e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs block">
            <span className="text-muted-foreground block mb-1">On-screen caption</span>
            <input type="text" value={local.caption} onChange={(e) => set("caption", e.target.value)}
              className="w-full bg-background border hairline px-2 py-1.5 text-sm"
              placeholder="Optional overlay text" />
          </label>
          <label className="text-xs block">
            <span className="text-muted-foreground block mb-1">Duration (sec)</span>
            <input type="number" min={2} max={30} value={local.duration}
              onChange={(e) => set("duration", parseInt(e.target.value, 10) || 6)}
              className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
          </label>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Stage 2 — Image + Cast -------------------- */
function StageImage({ registry, imageModelId, setImageModelId, castList, setCastList,
                      castIds, setCastIds, scenes, saveScene, onGenerate, regenImage,
                      onContinue, busy, openLibraryPick }) {
  const models = (registry?.image || []).filter((m) => m.enabled);
  const hasImages = scenes.some((s) => s.image_url || s.location_image_url);
  const selectedModel = models.find((m) => m.model_id === imageModelId);
  const { degraded, reason } = useEnvDegraded();
  const blockTitle = degraded ? `Disabled — fix environment first. ${reason}` : "";
  return (
    <div className="space-y-4" data-testid="sbs-stage-2">
      <ModelInUsePill stage="Image" model={selectedModel} testid="sbs-image-model-pill" />
      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Stage 2 · Pick image model + cast</div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="sbs-image-models">
          {models.map((m) => (
            <button key={m.id}
              onClick={() => setImageModelId(m.model_id)}
              data-testid={`sbs-image-model-${m.model_id}`}
              className={`text-left card-flat p-3 border-2 transition-colors
                ${m.model_id === imageModelId ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-transparent hover:border-muted-foreground/30"}`}>
              <div className="text-sm font-medium">{m.label}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{m.provider} · {m.model_id}</div>
              <div className="text-[11px] text-[hsl(var(--primary))] font-medium mt-2">
                ₹{m.cost_per_unit_inr}/{m.unit}
              </div>
            </button>
          ))}
        </div>
      </div>

      <CastPicker castList={castList} setCastList={setCastList}
        selectedIds={castIds} setSelectedIds={setCastIds} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          Generates a <b>character image</b> and a <b>separate location image</b> for every
          scene. Each is independently regenerable below.
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <CostChip stage="image" sceneCount={scenes.length || 1} model={selectedModel}
            extraUnits={2}
            label={`Character + location · ${scenes.length || 1} scenes`}
            testid="sbs-image-cost-chip" />
          <button onClick={onGenerate} disabled={busy || !scenes.length || degraded}
            title={blockTitle || undefined}
            className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="sbs-image-generate">
            <Sparkle size={12} weight="duotone" />
            {busy ? "Generating…" : (hasImages ? "Generate missing images" : "Generate images")}
          </button>
          {degraded && <EnvDegradedNote reason={reason} testid="sbs-image-env-note" />}
        </div>
      </div>

      {hasImages && (
        <div className="space-y-3" data-testid="sbs-image-scene-list">
          {scenes.map((s) => (
            <SceneImageCard key={s.id} scene={s} saveScene={saveScene}
              regenImage={regenImage} busy={busy}
              openLibraryPick={openLibraryPick} />
          ))}
          <div className="flex justify-end">
            <button onClick={onContinue}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
              data-testid="sbs-image-continue">
              Continue to Video <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SceneImageCard({ scene, saveScene, regenImage, busy, openLibraryPick }) {
  const [locPrompt, setLocPrompt] = useState(scene.location_image_prompt || "");
  const [imgPrompt, setImgPrompt] = useState(scene.image_prompt || "");
  const saveAll = async () => {
    await saveScene(scene.id, { image_prompt: imgPrompt, location_image_prompt: locPrompt });
    toast.success(`Scene ${scene.index} prompts saved.`);
  };
  const saveImgToLibrary = async (kind) => {
    try {
      const fd = new FormData();
      fd.append("scene_id", scene.id);
      fd.append("kind", kind);
      fd.append("name", `${scene.title || "Scene " + scene.index} · ${kind}`);
      await api.post("/admin/social/library/from-scene-image", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Saved ${kind} image to library.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const uploadImg = (kind) => async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("image", f);
      await api.post(`/admin/social/scenes/${scene.id}/upload-image`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${kind} image — reload scenes to see.`);
      // Force scene refresh by busting the cached image URL via local re-render.
      window.location.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <div className="card-flat p-4" data-testid={`sbs-image-card-${scene.id}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="eyebrow">Scene {scene.index} · {scene.title || "Untitled"}
          {scene.image_status === "rendering" && <span className="ml-2 text-[10px] text-[hsl(var(--secondary))]">· rendering…</span>}
          {scene.image_status === "queued" && <span className="ml-2 text-[10px] text-muted-foreground">· queued</span>}
        </div>
        <button onClick={saveAll} className="btn-ghost px-2 py-1 text-[10px]">Save prompts</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {/* Character */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
              <Camera size={11} weight="duotone" /> Character
            </div>
            <div className="inline-flex items-center gap-1">
              {openLibraryPick && (
                <button onClick={() => openLibraryPick(scene.id, "character")}
                  className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5"
                  title="Pick a character image from the library"
                  data-testid={`sbs-pick-character-${scene.id}`}>
                  <FolderOpen size={10} weight="duotone" />
                </button>
              )}
              {scene.image_url && (
                <button onClick={() => saveImgToLibrary("character")}
                  className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5"
                  title="Save to library"
                  data-testid={`sbs-save-character-${scene.id}`}>
                  <BookmarkSimple size={10} weight="duotone" />
                </button>
              )}
              <label className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5 cursor-pointer"
                title="Upload from disk"
                data-testid={`sbs-upload-character-${scene.id}`}>
                <UploadSimple size={10} weight="bold" />
                <input type="file" accept="image/*" onChange={uploadImg("character")} className="hidden" />
              </label>
              <button onClick={() => regenImage(scene.id, "character")} disabled={busy}
                className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5 disabled:opacity-40"
                data-testid={`sbs-regen-character-${scene.id}`}>
                <ArrowsClockwise size={10} weight="bold" /> Regen
              </button>
            </div>
          </div>
          {scene.image_url ? (
            <img src={scene.image_url} alt={`Scene ${scene.index} character`}
              className="w-full aspect-video object-cover border hairline" />
          ) : (
            <div className="w-full aspect-video bg-muted/30 border hairline border-dashed flex items-center justify-center text-[11px] text-muted-foreground">
              {scene.image_status === "rendering" ? "Rendering…" : "(Not generated yet)"}
            </div>
          )}
          <textarea rows={2} value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-2"
            placeholder="Image prompt for the character render" />
        </div>
        {/* Location */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
              <MapPin size={11} weight="duotone" /> Location
            </div>
            <div className="inline-flex items-center gap-1">
              {openLibraryPick && (
                <button onClick={() => openLibraryPick(scene.id, "location")}
                  className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5"
                  title="Pick a location/background image from the library"
                  data-testid={`sbs-pick-location-${scene.id}`}>
                  <FolderOpen size={10} weight="duotone" />
                </button>
              )}
              {scene.location_image_url && (
                <button onClick={() => saveImgToLibrary("location")}
                  className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5"
                  title="Save to library"
                  data-testid={`sbs-save-location-${scene.id}`}>
                  <BookmarkSimple size={10} weight="duotone" />
                </button>
              )}
              <label className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5 cursor-pointer"
                title="Upload from disk"
                data-testid={`sbs-upload-location-${scene.id}`}>
                <UploadSimple size={10} weight="bold" />
                <input type="file" accept="image/*" onChange={uploadImg("location")} className="hidden" />
              </label>
              <button onClick={() => regenImage(scene.id, "location")} disabled={busy}
                className="btn-ghost px-1.5 py-0.5 text-[10px] inline-flex items-center gap-0.5 disabled:opacity-40"
                data-testid={`sbs-regen-location-${scene.id}`}>
                <ArrowsClockwise size={10} weight="bold" /> Regen
              </button>
            </div>
          </div>
          {scene.location_image_url ? (
            <img src={assetUrl(scene.location_image_url)} alt={`Scene ${scene.index} location`}
              className="w-full aspect-video object-cover border hairline" />
          ) : (
            <div className="w-full aspect-video bg-muted/30 border hairline border-dashed flex items-center justify-center text-[11px] text-muted-foreground">
              (Not generated yet)
            </div>
          )}
          <textarea rows={2} value={locPrompt} onChange={(e) => setLocPrompt(e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-2"
            placeholder="Location/background prompt (no characters)" />
        </div>
      </div>
    </div>
  );
}

/* -------------------- Cast Picker (lean inline) -------------------- */
function CastPicker({ castList, setCastList, selectedIds, setSelectedIds }) {
  const [adding, setAdding] = useState(false);
  const toggle = (id) => {
    setSelectedIds(selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };
  return (
    <div className="card-flat p-5" data-testid="sbs-cast-picker">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="eyebrow mb-1">Cast (consistent across all scenes)</div>
          <p className="text-[11px] text-muted-foreground max-w-xl">
            Saved characters are reused across workflows. Toggle the ones who appear in this run.
          </p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-ghost px-3 py-1.5 text-xs"
          data-testid="sbs-cast-add">+ Add character</button>
      </div>
      {castList.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center border hairline">
          No saved characters yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-3">
          {castList.map((c) => {
            const sel = selectedIds.includes(c.id);
            return (
              <button key={c.id} onClick={() => toggle(c.id)}
                data-testid={`sbs-cast-card-${c.id}`}
                className={`text-left card-flat p-3 border-2 ${sel ? "border-[hsl(var(--primary))]" : "border-transparent hover:border-muted-foreground/30"}`}>
                <div className="flex items-center gap-3">
                  {c.reference_image_url ? (
                    <img src={assetUrl(c.reference_image_url)} alt={c.name} className="w-12 h-12 object-cover rounded border hairline" />
                  ) : (
                    <div className="w-12 h-12 bg-muted rounded flex items-center justify-center text-xs">{(c.name || "?")[0]}</div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate inline-flex items-center gap-1">
                      {c.name}
                      {c.is_preset && <span className="text-[8px] uppercase bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))] px-1 rounded">Preset</span>}
                      {c.lock_character_image && (
                        <span title="Character image locked">
                          <Lock size={10} weight="duotone" className="text-[hsl(var(--primary))]" />
                        </span>
                      )}
                      {c.lock_background_image && (
                        <span title="Background image locked">
                          <Lock size={10} weight="duotone" className="text-[#5FB37C]" />
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {[c.age, c.gender, c.role_label].filter(Boolean).join(" · ") || "Character"}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {adding && (
        <AddCastInline onClose={() => setAdding(false)}
          onCreated={(c) => { setCastList((l) => [c, ...l]); setAdding(false); }} />
      )}
    </div>
  );
}

function AddCastInline({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", role_label: "", description: "",
    age: "", gender: "", ethnicity: "", outfit: "", mood: "", style_note: "",
    voice_provider: "", voice_id: "", voice_label: "",
    seed: 73, is_preset: false,
    // Iter-39 round 3 — image locks
    lock_character_image: false, lock_background_image: false,
  });
  const [file, setFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (file) fd.append("image", file);
      if (bgFile) fd.append("background_image", bgFile);
      const { data } = await api.post("/admin/social/cast", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: list } = await api.get("/admin/social/cast");
      const created = list.find((x) => x.id === data.id);
      onCreated(created || { id: data.id, name: form.name });
      toast.success("Character saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const f = (label, key, opts = {}) => (
    <label className="text-xs block">
      <span className="text-muted-foreground block mb-1">{label}</span>
      <input type={opts.type || "text"} value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: opts.type === "number" ? (parseInt(e.target.value, 10) || 0) : e.target.value })}
        placeholder={opts.placeholder || ""}
        className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
    </label>
  );
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-2xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="sbs-cast-add-modal">
        <div className="eyebrow">Add / save character</div>
        <div className="grid grid-cols-2 gap-3">
          {f("Name *", "name", { placeholder: "e.g. Priya" })}
          {f("Role / archetype", "role_label", { placeholder: "e.g. Bengaluru engineer" })}
          {f("Age", "age", { placeholder: "e.g. 32" })}
          {f("Gender", "gender", { placeholder: "e.g. Female" })}
          {f("Ethnicity / look", "ethnicity", { placeholder: "e.g. South Indian, urban" })}
          {f("Outfit", "outfit", { placeholder: "e.g. Smart casual, kurta" })}
          {f("Mood / tone", "mood", { placeholder: "e.g. Thoughtful, optimistic" })}
          {f("Style note", "style_note", { placeholder: "Extra rendering style cue" })}
          {f("Voice provider", "voice_provider", { placeholder: "elevenlabs / openai" })}
          {f("Voice id", "voice_id", { placeholder: "ElevenLabs voice id" })}
          {f("Voice label", "voice_label", { placeholder: "e.g. Female · Indian English" })}
          {f("Image seed", "seed", { type: "number" })}
        </div>
        <label className="text-xs block">
          <span className="text-muted-foreground block mb-1">Description (image prompt cue)</span>
          <textarea rows={2} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_preset}
            onChange={(e) => setForm({ ...form, is_preset: e.target.checked })}
            data-testid="sbs-cast-is-preset" />
          Save as preset (re-use across future workflows)
        </label>
        {/* Iter-39 round 3 — image locks */}
        <div className="card-flat p-3 space-y-2 border-l-2 border-[hsl(var(--primary))]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
            <Lock size={10} weight="duotone" /> Image locks (skip Gemini regen — reuse the locked image as-is)
          </div>
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.lock_character_image}
              onChange={(e) => setForm({ ...form, lock_character_image: e.target.checked })}
              data-testid="sbs-cast-lock-character" />
            Lock character image (every scene's character uses the reference image above — zero LLM cost)
          </label>
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.lock_background_image}
              onChange={(e) => setForm({ ...form, lock_background_image: e.target.checked })}
              data-testid="sbs-cast-lock-background" />
            Lock background image (use the uploaded background as every scene's location)
          </label>
          <label className="text-xs block">
            <span className="text-muted-foreground block mb-1">Background reference image (PNG/JPG, 5 MB max)</span>
            <input type="file" accept="image/*" onChange={(e) => setBgFile(e.target.files?.[0] || null)}
              data-testid="sbs-cast-background-file" />
          </label>
        </div>
        <label className="text-sm block">
          <span className="block text-xs text-muted-foreground mb-1">Reference image (5 MB max)</span>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary px-4 py-1.5 text-xs disabled:opacity-50"
            data-testid="sbs-cast-save">{busy ? "Saving…" : "Save character"}</button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Stage 3 — Video -------------------- */
function StageVideo({ registry, videoModelId, setVideoModelId, scenes, saveScene,
                      onGenerate, regenVideo, deleteScene, onContinue, busy, openLibraryPick }) {
  const models = (registry?.video || []).filter((m) => m.enabled);
  const hasVideos = scenes.some((s) => s.video_url);
  const selectedModel = models.find((m) => m.model_id === videoModelId);
  const { degraded, reason } = useEnvDegraded();
  const blockTitle = degraded ? `Disabled — fix environment first. ${reason}` : "";
  return (
    <div className="space-y-4" data-testid="sbs-stage-3">
      <ModelInUsePill stage="Video" model={selectedModel} testid="sbs-video-model-pill" />
      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Stage 3 · Pick default video model</div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="sbs-video-models">
          {models.map((m) => (
            <button key={m.id}
              onClick={() => setVideoModelId(m.model_id)}
              data-testid={`sbs-video-model-${m.model_id}`}
              className={`text-left card-flat p-3 border-2 transition-colors
                ${m.model_id === videoModelId ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-transparent hover:border-muted-foreground/30"}`}>
              <div className="text-sm font-medium">{m.label}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{m.provider} · {m.model_id}</div>
              <div className="text-[11px] text-[hsl(var(--primary))] font-medium mt-2">
                ₹{m.cost_per_unit_inr}/{m.unit}
              </div>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Each scene can override the default model via the per-card dropdown below.
        </p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          Generates video for every included scene that's missing one. Per-scene regenerate,
          delete and model-swap controls live on each card.
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <CostChip stage="video" sceneCount={scenes.length || 1} model={selectedModel}
            label={`Render ${scenes.length || 1} clips`}
            testid="sbs-video-cost-chip" />
          <button onClick={onGenerate} disabled={busy || !scenes.length || degraded}
            title={blockTitle || undefined}
            className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="sbs-video-generate">
            <Lightning size={12} weight="duotone" />
            {busy ? "Rendering…" : (hasVideos ? "Render missing videos" : "Render all videos")}
          </button>
          {degraded && <EnvDegradedNote reason={reason} testid="sbs-video-env-note" />}
        </div>
      </div>

      {scenes.length > 0 && (
        <div className="space-y-3" data-testid="sbs-video-scene-list">
          {scenes.map((s) => (
            <SceneVideoCard key={s.id} scene={s} models={models}
              saveScene={saveScene} regenVideo={regenVideo}
              deleteScene={deleteScene} busy={busy}
              openLibraryPick={openLibraryPick ? () => openLibraryPick(s.id) : null} />
          ))}
          <div className="flex justify-end">
            <button onClick={onContinue}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
              data-testid="sbs-video-continue">
              Continue to Timeline <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SceneVideoCard({ scene, models, saveScene, regenVideo, deleteScene, busy, openLibraryPick }) {
  const [vPrompt, setVPrompt] = useState(scene.video_prompt || "");
  const [vModel, setVModel] = useState(scene.video_model_override || "");
  const [include, setInclude] = useState(scene.include !== false);
  const toggleInclude = async (val) => {
    setInclude(val);
    await saveScene(scene.id, { include: val });
  };
  const doRegen = () => {
    regenVideo(scene.id, {
      video_prompt: vPrompt,
      video_model_id: vModel || null,
    });
  };
  const saveVideoToLibrary = async () => {
    try {
      const fd = new FormData();
      fd.append("scene_id", scene.id);
      fd.append("name", scene.title || `Scene ${scene.index} video`);
      await api.post("/admin/social/library/from-scene-video", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video saved to library.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData();
      fd.append("video", f);
      await api.post(`/admin/social/scenes/${scene.id}/upload-video`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded.");
      window.location.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <div className={`card-flat p-4 ${include ? "" : "opacity-50"}`}
      data-testid={`sbs-video-card-${scene.id}`}>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="eyebrow">Scene {scene.index} · {scene.title || "Untitled"} · <span className="text-muted-foreground">{scene.status}</span></div>
        <div className="flex items-center gap-2">
          {openLibraryPick && (
            <button onClick={openLibraryPick}
              className="btn-ghost px-2 py-1 text-[10px] inline-flex items-center gap-1"
              title="Replace this scene's video with a saved clip from the library."
              data-testid={`sbs-pick-video-${scene.id}`}>
              <FolderOpen size={10} weight="duotone" /> Use from library
            </button>
          )}
          {scene.video_url && (
            <button onClick={saveVideoToLibrary}
              className="btn-ghost px-2 py-1 text-[10px] inline-flex items-center gap-1"
              title="Save video to library"
              data-testid={`sbs-save-video-${scene.id}`}>
              <BookmarkSimple size={10} weight="duotone" /> Save
            </button>
          )}
          <label className="btn-ghost px-2 py-1 text-[10px] inline-flex items-center gap-1 cursor-pointer"
            title="Upload MP4 from disk"
            data-testid={`sbs-upload-video-${scene.id}`}>
            <UploadSimple size={10} weight="bold" /> Upload
            <input type="file" accept="video/mp4,video/*" onChange={uploadVideo} className="hidden" />
          </label>
          <label className="text-[11px] inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={include} onChange={(e) => toggleInclude(e.target.checked)}
              data-testid={`sbs-video-include-${scene.id}`} />
            Include
          </label>
          <button onClick={() => deleteScene(scene.id)} disabled={busy}
            className="btn-ghost px-2 py-1 text-[10px] text-destructive inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`sbs-video-delete-${scene.id}`}>
            <Trash size={10} weight="bold" /> Delete
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          {scene.video_url ? (
            <video src={assetUrl(scene.video_url)} controls className="w-full aspect-video border hairline bg-black"
              data-testid={`sbs-video-preview-${scene.id}`} />
          ) : (
            <div className="w-full aspect-video bg-muted/30 border hairline border-dashed flex items-center justify-center text-[11px] text-muted-foreground">
              {scene.status === "regenerating" || scene.status === "queued" ? "Rendering…" : "(No video yet)"}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <label className="text-xs block">
            <span className="text-muted-foreground block mb-1">Video prompt</span>
            <textarea rows={3} value={vPrompt} onChange={(e) => setVPrompt(e.target.value)}
              className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs block">
            <span className="text-muted-foreground block mb-1">Model override (optional)</span>
            <select value={vModel} onChange={(e) => setVModel(e.target.value)}
              className="w-full bg-background border hairline px-2 py-1.5 text-sm"
              data-testid={`sbs-video-model-override-${scene.id}`}>
              <option value="">(use draft default)</option>
              {models.map((m) => (
                <option key={m.id} value={m.model_id}>{m.label} · ₹{m.cost_per_unit_inr}/{m.unit}</option>
              ))}
            </select>
          </label>
          <div className="flex justify-end">
            <button onClick={doRegen} disabled={busy || !include}
              className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`sbs-video-regen-${scene.id}`}>
              <ArrowsClockwise size={11} weight="bold" /> Regenerate video
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Stage 4 — Hand off to timeline -------------------- */
function StageTimelineHandoff({ draftId, onJumpToDraft }) {
  return (
    <div className="card-flat p-6 text-center space-y-3" data-testid="sbs-stage-4">
      <Check size={32} weight="duotone" className="mx-auto text-[hsl(var(--primary))]" />
      <div className="font-serif text-2xl">All scenes ready</div>
      <p className="text-xs text-muted-foreground max-w-md mx-auto">
        Open the draft to fine-tune the timeline — drag-drop reorder, trim clips, add overlay
        text, drop background music, then approve & publish.
      </p>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border hairline text-[11px] bg-muted/30"
        data-testid="sbs-timeline-cost-chip"
        title="Timeline editing, trimming, ffmpeg overlays and publishing do not call any LLM provider — no universal-key debit.">
        <Check size={11} weight="bold" className="text-[hsl(var(--primary))]" />
        <span className="font-medium">Timeline + publish</span>
        <span className="text-muted-foreground">·</span>
        <span className="font-medium">₹0 · $0</span>
        <span className="text-muted-foreground">(ffmpeg only, no LLM calls)</span>
      </div>
      <div>
        <button onClick={() => onJumpToDraft && onJumpToDraft(draftId)}
          className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
          data-testid="sbs-jump-to-timeline">
          Open draft in Timeline <ArrowRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
